/**
 * The one tone treatment every menu / list-of-choices row in `apps/app` wears.
 *
 * WHY THIS FILE EXISTS. The app had one row treatment that was right — the
 * "Layout" popover's column toggles (`DisplayedDataPopover`) — and ~20 that were
 * not, in four different flavours: black-at-rest with a grey background on hover
 * and no text change at all (`ActionMenu`, `Menu`, `SortDrawer`,
 * `MoreOptionsMenu`'s sheet); a purple tint nobody chose
 * (`getPickerMenuProps`); hover deliberately switched off (`SelectField`); and
 * several rows with no hover whatsoever (`PickerListItem`,
 * `ProvenanceOwnerSearchPicker`, `CertificatesSearchPicker`). One app, five
 * answers to "what does a menu row look like when you point at it".
 *
 * The values below are MEASURED from the Layout popover in the browser, not
 * transcribed from its source:
 *
 *   unselected, at rest   rgb(122,122,122)  #7A7A7A   `text.secondary`
 *   selected              rgb(0,0,0)        #000000   `text.primary`
 *   hover / focus         `grey[50]` background + the brand accent on the text
 *
 * THE TWO "DARK" TOKENS ARE DELIBERATELY DIFFERENT, and this is the one thing
 * not to collapse while tidying. Selected reads `text.primary`; hover reads
 * `brand[600] || primary.main`. On a tenant that has set no `primary_color` the
 * brand ramp resolves to black (`settings/mui/brandRamp.ts`), so the two are the
 * same pixel and the distinction looks redundant — it is not. On a themed tenant
 * "chosen" stays neutral-dark while "you are pointing at this" picks up the
 * brand's own colour, which is what makes the hover read as an affordance rather
 * than a second selection. Collapsing them to one token would only be visible on
 * the tenants we do not develop against.
 *
 * WHY LAYER 2 AND NOT `@repo/ui` (working-conventions §47). The obvious home is
 * `MENU_ITEM_SX` in `packages/ui/theme/menuChrome.ts`, which every menu in BOTH
 * apps already spreads. It was rejected on the evidence: that constant carries
 * spacing only, by explicit design ("after your own overrides if you need to
 * change hover/selected colors, since those are a different concern from
 * spacing"), and the platform does not do what this does. `apps/platform`'s
 * `ActionDropdown` brightens a hovered row to `text.primary` — neutral, no
 * accent — and nothing over there tints a menu row with the brand colour. Moving
 * this treatment down a layer would therefore not be "improving the base", it
 * would be pushing an app-specific decision onto ~40 platform menus that never
 * asked for it. The rest-state half (`text.secondary` when unselected) is the
 * half the two apps agree on; if the platform ever wants the accent hover too,
 * THAT is the moment to promote this file into `menuChrome.ts`.
 *
 * HOW TO USE IT.
 *
 *   sx={theme => ({ ...menuRowToneSx(theme, { selected }) })}
 *
 * and give the row's PRIMARY label `sx={MENU_ROW_LABEL_SX}` so it inherits.
 * Secondary/sublabel text is intentionally NOT swept into the inherit — a row
 * like `LocationSearchPicker`'s has a muted second line that must stay muted, and
 * a blanket `& .MuiTypography-root { color: inherit }` would drag it to full
 * contrast on hover. The reference popover gets away with the blanket rule only
 * because its rows contain exactly one `Typography`.
 *
 * Trailing glyphs (the selected check, a chevron) should draw in `currentColor`
 * so they travel with the row for free. Several hardcoded `#000000` — those are
 * the ticks that stayed black while the label around them changed colour.
 */

import { alpha } from '@mui/material'

type ToneOpts = {
  /** Row is the currently-chosen option. Action menus leave this false. */
  selected?: boolean
  /** Row is a destructive action (Delete, Log out) — red, per §32. */
  destructive?: boolean
  /** Row cannot be actioned: muted further, and no hover at all. */
  disabled?: boolean
  /**
   * Opt in to a resting BACKGROUND for the selected row, on top of the colour.
   *
   * Off by default, because the reference popover deliberately has none —
   * selection there is carried by the text colour and the trailing check, and a
   * wash as well would be saying it three times.
   *
   * On for rows that already had one (the country/gender pickers, the location
   * multi-select), where removing it would be an unrelated redesign. It exists
   * because those rows had a real bug: their selected background and their
   * hover background were BOTH `grey[50]`, so pointing at a selected row erased
   * the only cue that it was selected. Taking both backgrounds means the two can
   * be kept one step apart by construction — `grey[100]` at rest, `grey[200]`
   * hovered — instead of each call site picking a pair and getting it wrong.
   */
  selectedBackground?: boolean
}

/**
 * `brand[600]` — the tenant's own `primary_color`, unmixed.
 *
 * ON AN UNTHEMED TENANT THIS IS BLACK, NOT VERA PURPLE, and the second half of
 * that sentence is the one to check before "simplifying" this line. `brandRamp`
 * (`settings/mui/brandRamp.ts`) falls back to `[0,0,0]` and says so in its own
 * docstring — *"never Vera purple, since silently reverting to it is the exact
 * failure this module removes"* — and `palette.ts` wires `brand:
 * brandRamp(theme?.primary_color)` after the `basePalette` spread specifically so
 * `@repo/ui` components stop focusing purple in a brand's own site.
 *
 * The `||` therefore never fires: `brandRamp` always returns a complete ramp, so
 * `brand[600]` is always truthy. `primary.main` is kept only as a guard for a
 * theme object that has no `brand` at all, and is NOT the practical value —
 * which matters, because `primary.main` DOES fall back to Vera purple
 * (`palette.ts`: `theme?.primary_color || basePalette.brand[600]`, VERA-304).
 * Reading this line as "purple when unthemed" is the available misreading; one
 * reviewer already made it. Verified live: black.
 *
 * That neutrality is the point. This app renders inside somebody else's site, so
 * an unthemed accent must not assert a colour nobody chose — the same reasoning
 * behind the black primary and the neutral field focus. It is also why the
 * hardcoded `rgba(127,72,223,…)` hover in `getPickerMenuProps` had to go rather
 * than be reused here.
 */
export const menuAccentColor = (theme: any) =>
  theme?.palette?.brand?.[600] || theme?.palette?.primary?.main

/** Apply to a row's primary label so it follows the row's colour. */
export const MENU_ROW_LABEL_SX = { color: 'inherit' } as const

/**
 * The row's colour contract: muted at rest, dark when selected, accent + grey
 * wash on hover and keyboard focus.
 *
 * Spread this into a row's `sx`. It sets `color` on the ROW (not the label), so
 * anything inside drawing in `currentColor` — the check, a leading icon — comes
 * along without extra wiring.
 *
 * `:focus-visible` is paired with `:hover` throughout: a keyboard user never
 * fires `:hover`, and a row whose only affordance is a pointer state is a row
 * they cannot see themselves on.
 */
export const menuRowToneSx = (theme: any, opts: ToneOpts = {}) => {
  const { selected = false, destructive = false, disabled = false, selectedBackground = false } = opts

  const restColor = destructive
    ? theme.palette.error.main
    : selected
      ? theme.palette.text.primary
      : theme.palette.text.secondary

  const activeColor = destructive ? theme.palette.error.main : menuAccentColor(theme)

  if (disabled) {
    return {
      color: theme.palette.text.disabled,
      // Stated rather than omitted: a disabled row inside a list whose siblings
      // all react needs its inertness to be explicit, or MUI's own
      // ListItemButton/MenuItem default hover leaks back in.
      '&:hover, &:focus-visible': { backgroundColor: 'transparent', color: theme.palette.text.disabled },
    }
  }

  // A selected row that opts into a background sits one step darker than the
  // hover wash, so hovering it moves it FURTHER from the unselected rows rather
  // than onto them. The bug this replaces had both at `grey[50]`.
  const hoverBackground = destructive
    ? alpha(theme.palette.error.main, 0.08)
    : selected && selectedBackground
      ? theme.palette.grey[200]
      : theme.palette.grey[50]

  return {
    color: restColor,
    ...(selectedBackground && {
      backgroundColor: selected ? theme.palette.grey[100] : 'transparent',
    }),
    // Colour AND background, so the row does not snap to its accent while the
    // wash fades in behind it. 0.15s matches the app's other row transitions.
    transition: 'color 0.15s ease-in-out, background-color 0.15s ease-in-out',
    '&:hover, &:focus-visible': {
      backgroundColor: hoverBackground,
      color: activeColor,
    },
  }
}
